import { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setPreferences, clearUserData } from "../store/slices/userSlice";
import { manualLogout, setGlobalError } from "../store/slices/authSlice";
import {
  fetchJournals,
  FetchPreferencesJournal,
  fetchPreferred,
} from "../services/apiService/journalApi";
import PreferenceModal from "./ui/PreferenceModal";
import useToaster from "../hooks/useToaster";
import { useNavigate, useLocation } from "react-router-dom";
import { HeaderSkeleton } from "./ui/Skeleton";
import {
  fetchUsersById,
  updateUserPreferences,
} from "../services/apiService/userApi";
import ScooterLogo from "../assets/HeaderLogo.png";
import settingUser from "../assets/settinguser.png";
import { API_BASE_URL } from "../services/apiService/axiosInstance";
import { logoutSSO } from "../services/apiService/authApi";
import DataSyncFailed from "./edgeCases/DataSyncFailed";
import userLogo from "../assets/userLogo.jpg";

/**
 * Main application header with user controls and navigation
 * @param {boolean} isLoading - Shows loading skeleton when true
 * @param {function} toggleSidebar - Toggles sidebar visibility
 * @param {boolean} isSidebarOpen - Current sidebar state
 */
const Header = ({ isLoading, toggleSidebar, isSidebarOpen }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPreferenceOpen, setIsPreferenceOpen] = useState(false);
  const [journals, setJournals] = useState([]);
  const [preferredJournals, setPreferredJournals] = useState([]);
  const [assignedJournals, setAssignedJournals] = useState([]);
  const [otherJournals, setOtherJournals] = useState([]);
  let [users, setUsers] = useState({});
  let [loading, setLoading] = useState(false);

  const showToast = useToaster();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const { userData, preferences } = useSelector((state) => state.user);

  useEffect(() => {
    setPreferredJournals(preferences);
    setUsers(userData);
  }, [preferences, userData]);

  /**
   * fetches journals for preferences from api
   * @async
   * @listens - userData
   * @function fetchData
   */
  const fetchJournalData = async () => {
    try {
      let user = await fetchUsersById(userData.id);
      setPreferredJournals(user?.data?.preferences);
      const journalRes = await fetchPreferred();
      const journalsList = journalRes?.data || [];

      setJournals(journalsList);

      let articleRes = await FetchPreferencesJournal(userData.id);

      const articles = articleRes?.data;

      const assigned = journalsList.filter((j) => articles.includes(j.value));
      const others = journalsList.filter((j) => !articles.includes(j.value));

      setAssignedJournals(assigned);
      setOtherJournals(others);
    } catch (err) {
      console.error("Error fetching data:", err);
      dispatch(
        setGlobalError({ globalError: true, originUrl: location.pathname })
      );
    }
  };

  /** Toggles the user profile dropdown */
  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  /**
   * Handles user logout and redirects to login
   */
  const handleLogout = () => {
    dispatch(manualLogout());
    dispatch(clearUserData());
    setIsDropdownOpen(false);

    // navigate("/login")

    window.location.href = `${API_BASE_URL}/logout`;
    // logoutSSO()
  };

  /** Opens the journal preferences modal */
  const openPreferences = async () => {
    setLoading(true);
    await fetchJournalData();
    setLoading(false);
    setIsPreferenceOpen(true);
    setIsDropdownOpen(false);
  };

  /**
   * Saves user's journal preferences
   * @param {string[]} data - Array of preferred journal names
   */
  const closePreferences = async (data) => {
    try {
      let updateData = { ...users, preferences: data };
      let response = await updateUserPreferences(userData.id, updateData);
      if (response.success) {
        dispatch(setPreferences(data));
        setUsers(response.data);
        setIsPreferenceOpen(false);
        showToast("Journal preference updated successfully");
      } else {
        showToast("error updating preferences");
      }
    } catch (err) {
      showToast(err);
    }
  };

  /** Closes preferences modal without saving */
  let closeModel = () => {
    setIsPreferenceOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <header className="bg-white p-4 flex items-center border-b border-[#B7B7B7] w-full h-[72px]">
        <button
          className="flex items-center justify-center mt-auto w-[40px] mr-3 rounded hover:bg-[#EDF0FC] text-[#35424D]"
          onClick={toggleSidebar}
        >
          <i className="fas fa-bars w-[30px] h-[20px] text-[20px]"></i>
        </button>

        <div className="relative flex items-center">
          <img
            src={ScooterLogo}
            alt="Scooter Logo"
            className="w-[168px] h-[40px] object-contain"
          />
        </div>

        <div className="flex-grow flex justify-center items-center">
          <DataSyncFailed />
        </div>

        <div className="relative" ref={dropdownRef}>
          {isLoading ? (
            <HeaderSkeleton />
          ) : (
            <div
              className="flex items-center border h-[48px] border-[#C2C6CA] p-1 rounded cursor-pointer hover:bg-blue-100"
              onClick={toggleDropdown}
            >
              <img
                alt="User avatar"
                className="rounded-full mr-2 w-[32px] h-[32px]"
                src={userLogo}
              />
              <div>
                <span className="block text-[#35424D] text-[13px] font-semibold">
                  {`${userData?.firstName} ${userData?.lastName}`}
                </span>
                <span className="block text-[#5D6871] text-[10px] font-medium">
                  {userData?.email}
                </span>
              </div>
            </div>
          )}

          {!isLoading && isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-[250px] h-[88px] bg-white border border-gray-300 rounded-lg shadow-lg z-[999] flex flex-col justify-between py-1">
              <div
                className="h-[40px] flex items-center px-4 text-sm text-[#35424D] hover:bg-[#E1E6FF] cursor-pointer gap-2"
                onClick={openPreferences}
              >
                <img src={settingUser} alt="settings" />
                Preference Settings
              </div>
              <div className="border-t border-gray-300"></div>
              <div
                className="h-[40px] flex items-center px-4 text-sm text-[#35424D] hover:bg-[#E1E6FF] cursor-pointer gap-2"
                onClick={handleLogout}
              >
                <i className="fas fa-sign-out-alt"></i>
                Logout
              </div>
            </div>
          )}
        </div>
      </header>

      {isPreferenceOpen && (
        <PreferenceModal
          isOpen={isPreferenceOpen}
          closeModal={closePreferences}
          journals={journals}
          preferredJournals={preferredJournals}
          setPreferredJournals={setPreferredJournals}
          assignedJournals={assignedJournals}
          otherJournals={otherJournals}
          onClose={closeModel}
          loading={loading}
        />
      )}
    </>
  );
};

export default Header;
